import type { RichTextDoc } from '@bcecloud/portal-ui';
import { api, getJson, getList, sendJson } from '@/lib/api';
import type { OrganizationSummary } from '@/lib/types';

/**
 * The Resource center (Roy's portal list, item 13; design in
 * docs/planning/features/resource-center/README.md). Mirrors the API's
 * `resources.domain.ts`: statuses, formats, audiences, limits and the publish
 * rule. The API is the authority; the copies let the form say what is wrong
 * before anyone presses Publish.
 */

export const RESOURCE_STATUS = {
  draft: 'DRAFT',
  published: 'PUBLISHED',
  archived: 'ARCHIVED',
} as const;
export type ResourceStatus = (typeof RESOURCE_STATUS)[keyof typeof RESOURCE_STATUS];

export const RESOURCE_FORMAT = {
  article: 'ARTICLE',
  file: 'FILE',
  link: 'LINK',
} as const;
export type ResourceFormat = (typeof RESOURCE_FORMAT)[keyof typeof RESOURCE_FORMAT];

export const RESOURCE_FORMAT_OPTIONS: readonly { readonly key: ResourceFormat; readonly label: string }[] = [
  { key: RESOURCE_FORMAT.article, label: 'Article' },
  { key: RESOURCE_FORMAT.file, label: 'File' },
  { key: RESOURCE_FORMAT.link, label: 'Link' },
];

export const RESOURCE_AUDIENCE = {
  allClients: 'ALL_CLIENTS',
  chosenClients: 'CHOSEN_CLIENTS',
} as const;
export type ResourceAudienceScope = (typeof RESOURCE_AUDIENCE)[keyof typeof RESOURCE_AUDIENCE];

/** Field lengths, matching the API's `RESOURCE_LIMITS`. */
export const RESOURCE_LIMITS = {
  title: 120,
  summary: 280,
  tag: 40,
  tags: 12,
  url: 2048,
  topicName: 60,
  topicDescription: 200,
} as const;

/** The upload cap, matching the API. Video goes in as a link. */
export const RESOURCE_FILE_MAX_BYTES = 25 * 1024 * 1024;

/** What the file picker offers: documents and images. */
export const RESOURCE_FILE_ACCEPT =
  '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp';

/** The icons a topic may use, matching the API's `RESOURCE_TOPIC_ICONS`. */
export const RESOURCE_TOPIC_ICONS = [
  'sparkles',
  'lightbulb',
  'mic',
  'circle-help',
  'messages',
  'compass',
  'megaphone',
  'heart-handshake',
  'book-marked',
  'wifi',
  'file-text',
  'book-open',
] as const;
export type ResourceTopicIcon = (typeof RESOURCE_TOPIC_ICONS)[number];

export interface ResourceOrganization {
  readonly id: string;
  readonly name: string;
}

export interface ResourceStats {
  readonly views: number;
  readonly downloads: number;
  readonly linkOpens: number;
}

/** A resource, as `/resources` lists it. */
export interface Resource {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly summary: string;
  readonly format: ResourceFormat;
  readonly status: ResourceStatus;
  readonly topic: { readonly id: string; readonly name: string } | null;
  readonly tags: readonly string[];
  readonly audience: { readonly scope: ResourceAudienceScope; readonly organizations: readonly ResourceOrganization[] };
  readonly formatLine: string;
  readonly readingMinutes: number | null;
  readonly file: {
    readonly name: string;
    readonly type: string;
    readonly kind: string;
    readonly size: number;
    readonly pageCount: number | null;
  } | null;
  readonly link: { readonly url: string; readonly host: string } | null;
  readonly publishedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly stats: ResourceStats;
}

/** One resource with its article and what stops it publishing. */
export interface ResourceDetail extends Resource {
  readonly body: RichTextDoc | null;
  readonly publishProblems: readonly string[];
}

/** A row on an organization's Resources tab. */
export interface OrganizationResource extends Resource {
  readonly alsoSharedWith: readonly ResourceOrganization[];
  readonly organizationViews: number;
  readonly lastOpenedAt: string | null;
}

export interface ResourceTopic {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly description: string;
  readonly icon: ResourceTopicIcon;
  readonly sortOrder: number;
  readonly archived: boolean;
  readonly resourceCount: number;
}

/** What the editor sends. */
export interface ResourceInput {
  readonly title: string;
  readonly summary: string;
  readonly format: ResourceFormat;
  readonly topicId: string;
  readonly tags: readonly string[];
  readonly audience:
    | { readonly scope: typeof RESOURCE_AUDIENCE.allClients }
    | { readonly scope: typeof RESOURCE_AUDIENCE.chosenClients; readonly organizationIds: readonly string[] };
  readonly body?: RichTextDoc | null;
  readonly url?: string | null;
}

/** A client organization the audience picker offers, with its portal address for Preview. */
export interface ClientOrganization extends ResourceOrganization {
  readonly portalUrl: string;
}

export const RESOURCES_QUERY_KEY = ['resources'] as const;
export const RESOURCE_TOPICS_QUERY_KEY = ['resource-topics'] as const;
export const resourceQueryKey = (id: string) => ['resource', id] as const;
export const organizationResourcesQueryKey = (organizationId: string) => ['organization-resources', organizationId] as const;

export const listResources = (signal?: AbortSignal): Promise<Resource[]> => getJson<Resource[]>('/resources', undefined, signal);
export const readResource = (id: string, signal?: AbortSignal): Promise<ResourceDetail> =>
  getJson<ResourceDetail>(`/resources/${id}`, undefined, signal);
export const createResource = (input: ResourceInput): Promise<ResourceDetail> => sendJson<ResourceDetail>('post', '/resources', input);
export const updateResource = (id: string, input: Partial<ResourceInput>): Promise<ResourceDetail> =>
  sendJson<ResourceDetail>('patch', `/resources/${id}`, input);

/** The status changes the editor offers. */
export const RESOURCE_ACTION = {
  publish: 'publish',
  unpublish: 'unpublish',
  archive: 'archive',
  restore: 'restore',
} as const;
export type ResourceAction = (typeof RESOURCE_ACTION)[keyof typeof RESOURCE_ACTION];

export const changeResourceStatus = (id: string, action: ResourceAction): Promise<ResourceDetail> =>
  sendJson<ResourceDetail>('post', `/resources/${id}/${action}`);

/**
 * Uploads a file resource's file (multipart; the API holds the bucket).
 *
 * @param id - The resource.
 * @param file - The file.
 * @returns The resource with its new file.
 */
export async function uploadResourceFile(id: string, file: File): Promise<ResourceDetail> {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post<{ data: ResourceDetail }>(`/resources/${id}/file`, form);
  return response.data.data;
}

export const listResourceTopics = (signal?: AbortSignal): Promise<ResourceTopic[]> =>
  getJson<ResourceTopic[]>('/resource-topics', undefined, signal);
export const createResourceTopic = (input: { name: string; description: string; icon: ResourceTopicIcon }): Promise<ResourceTopic> =>
  sendJson<ResourceTopic>('post', '/resource-topics', input);
export const updateResourceTopic = (
  id: string,
  input: Partial<{ name: string; description: string; icon: ResourceTopicIcon; sortOrder: number; archived: boolean }>,
): Promise<ResourceTopic> => sendJson<ResourceTopic>('patch', `/resource-topics/${id}`, input);

export const listOrganizationResources = (organizationId: string, signal?: AbortSignal): Promise<OrganizationResource[]> =>
  getJson<OrganizationResource[]>(`/organizations/${organizationId}/resources`, undefined, signal);
export const shareResources = (
  organizationId: string,
  resourceIds: readonly string[],
): Promise<{ shared: string[]; alreadyEveryClient: string[] }> =>
  sendJson('post', `/organizations/${organizationId}/resources`, { resourceIds });

/** The picker lists up to the API's page limit. */
const CLIENT_PICKER_LIMIT = 100;

export async function listClientOrganizations(signal?: AbortSignal): Promise<ClientOrganization[]> {
  const { items } = await getList<OrganizationSummary>(
    '/organizations',
    { kind: 'CLIENT', status: 'ACTIVE', limit: CLIENT_PICKER_LIMIT },
    signal,
  );
  return items
    .map((organization) => ({ id: organization.id, name: organization.name, portalUrl: organization.portalUrl }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** "Every client", or the client names: per client is the exception, so it reads bold in the list. */
export function audienceLabel(resource: Pick<Resource, 'audience'>): string {
  return resource.audience.scope === RESOURCE_AUDIENCE.allClients
    ? 'Every client'
    : resource.audience.organizations.map((organization) => organization.name).join(', ');
}

/**
 * Whether a link may be saved: https only, no credentials. Mirrors the API.
 *
 * @param url - What was typed.
 * @returns True when the API will take it.
 */
export function isHttpsUrl(url: string): boolean {
  const value = url.trim();
  if (value === '' || /\s/.test(value)) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && parsed.hostname !== '' && parsed.username === '' && parsed.password === '';
  } catch {
    return false;
  }
}

/**
 * The address a staff member opens to see a resource exactly as one client
 * will: the admin app's portal door mints a staff session for that portal and
 * lands on the reader page, drafts included.
 *
 * @param portalUrl - The client's portal address.
 * @param slug - The resource's slug.
 * @returns The admin-app path that opens the preview.
 */
export function previewPath(portalUrl: string, slug: string): string {
  return `/portal-access?to=${encodeURIComponent(`${portalUrl.replace(/\/$/, '')}/resources/${slug}`)}`;
}

/** The editor's working copy. */
export interface ResourceDraft {
  readonly title: string;
  readonly summary: string;
  readonly format: ResourceFormat;
  readonly topicId: string;
  readonly tags: readonly string[];
  readonly audience: ResourceAudienceScope;
  readonly organizationIds: readonly string[];
  readonly body: RichTextDoc | null;
  readonly url: string;
}

export function emptyResourceDraft(organizationId: string | null): ResourceDraft {
  return {
    title: '',
    summary: '',
    format: RESOURCE_FORMAT.article,
    topicId: '',
    tags: [],
    audience: organizationId === null ? RESOURCE_AUDIENCE.allClients : RESOURCE_AUDIENCE.chosenClients,
    organizationIds: organizationId === null ? [] : [organizationId],
    body: null,
    url: '',
  };
}

export function draftOfResource(resource: ResourceDetail): ResourceDraft {
  return {
    title: resource.title,
    summary: resource.summary,
    format: resource.format,
    topicId: resource.topic?.id ?? '',
    tags: resource.tags,
    audience: resource.audience.scope,
    organizationIds: resource.audience.organizations.map((organization) => organization.id),
    body: resource.body,
    url: resource.link?.url ?? '',
  };
}

/** What the editor sends for a draft: only the current format's content. */
export function inputOfDraft(draft: ResourceDraft): ResourceInput {
  return {
    title: draft.title.trim(),
    summary: draft.summary.trim(),
    format: draft.format,
    topicId: draft.topicId,
    tags: draft.tags,
    audience:
      draft.audience === RESOURCE_AUDIENCE.allClients
        ? { scope: RESOURCE_AUDIENCE.allClients }
        : { scope: RESOURCE_AUDIENCE.chosenClients, organizationIds: draft.organizationIds },
    ...(draft.format === RESOURCE_FORMAT.article ? { body: draft.body } : {}),
    ...(draft.format === RESOURCE_FORMAT.link ? { url: draft.url.trim() } : {}),
  };
}

/** What stops a draft from being saved at all, field by field. */
export function saveProblems(draft: ResourceDraft): Partial<Record<keyof ResourceDraft, string>> {
  const url = draft.url.trim();
  return {
    ...(draft.title.trim() === '' ? { title: 'Add a title.' } : {}),
    ...(draft.summary.trim() === '' ? { summary: 'Add a summary.' } : {}),
    ...(draft.topicId === '' ? { topicId: 'Choose a topic.' } : {}),
    ...(draft.audience === RESOURCE_AUDIENCE.chosenClients && draft.organizationIds.length === 0
      ? { organizationIds: 'Choose at least one client.' }
      : {}),
    ...(draft.format === RESOURCE_FORMAT.link && url !== '' && !isHttpsUrl(url) ? { url: 'Use a full https:// address.' } : {}),
  };
}

/**
 * Human file size: "3.1 MB", "820 KB".
 *
 * @param bytes - The size.
 * @returns The size in words.
 */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * A date as the list shows it: "Sep 24, 2026".
 *
 * @param iso - An ISO timestamp, or null.
 * @returns The date, or an empty string.
 */
export function shortDate(iso: string | null): string {
  if (iso === null) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
}
